/**
 * Changer le rythme d'une tournée (lot U4, ADR-0016) : la logique SANS écran.
 *
 * La fréquence et le premier jour d'une tournée ne se modifient jamais (la base le refuse) : changer de rythme, c'est ARRÊTER la
 * tournée et en créer une autre au nouveau rythme, d'un seul geste. L'écran le DIT avant d'agir (combien d'interventions à venir
 * s'annulent, quand la nouvelle démarre) ; le serveur calcule les dates et reprend tout le reste (site, machines, habituels,
 * remarque, n° de commande, durée, forfait). Le même geste change le JOUR à rythme égal (Benjamin, 2026-11-02 : le client passait
 * le 15 du mois, il veut le 25) : le rythme actuel est prérempli, on change le jour.
 */
import type { TourneeRythmeChange } from '../api/client';
import { jourFr, jourValide } from './planning';
import { interventionsQuAnnuleraLArret } from './planning-interventions';
import { MOIS_MAX, MOIS_MIN, libelleFrequence } from './planning-tournees';

/** Ce que le rythme lit d'une tournée : ses occurrences (état, jour, nombre d'interventions) — rien d'autre. */
export interface OccurrencesDeTournee {
  readonly occurrences: readonly { readonly status: string; readonly plannedDate: string | null; readonly interventions: number }[];
}

/** Le rythme ACTUEL d'une tournée : sa fréquence, son premier jour et ses occurrences. */
export interface RythmeActuel extends OccurrencesDeTournee {
  readonly frequencyMonths: number;
  readonly firstDay: string;
}

export interface SaisieRythme {
  /** Le nouveau rythme en mois, tel que saisi. Prérempli avec le rythme actuel : changer le jour est le cas courant. */
  readonly moisTexte: string;
  /** Le jour de la première occurrence de la NOUVELLE tournée, AAAA-MM-JJ. */
  readonly premierJour: string;
}

/**
 * Le jour proposé : celui de la première occurrence encore à faire d'aujourd'hui ou plus tard (la nouvelle tournée reprend là où
 * l'ancienne en était) ; à défaut, aujourd'hui. Une proposition modifiable, jamais imposée.
 */
export function premierJourPropose(
  occurrences: OccurrencesDeTournee['occurrences'],
  aujourdhui: string,
): string {
  const dates = occurrences
    .filter((o) => o.status === 'OPEN' && o.plannedDate !== null && o.plannedDate >= aujourdhui)
    .map((o) => o.plannedDate as string)
    .sort();
  return dates[0] ?? aujourdhui;
}

export function saisieRythmeDepart(t: RythmeActuel, aujourdhui: string): SaisieRythme {
  return { moisTexte: String(t.frequencyMonths), premierJour: premierJourPropose(t.occurrences, aujourdhui) };
}

/**
 * Le même rythme ET le même jour : rien ne changerait. Même jour = même jour du mois que le premier jour actuel, ou une date déjà
 * prévue pour une occurrence. Un autre jour du mois à rythme égal est permis (la même règle que le serveur).
 */
export function memeRythmeEtMemeJour(actuel: RythmeActuel, mois: number, premierJour: string): boolean {
  if (mois !== actuel.frequencyMonths) return false;
  if (actuel.firstDay.slice(8, 10) === premierJour.slice(8, 10)) return true;
  return actuel.occurrences.some((o) => o.plannedDate === premierJour);
}

export type LectureRythme =
  | { readonly ok: true; readonly frequencyMonths: number; readonly firstDay: string }
  | { readonly ok: false; readonly erreur: string };

/**
 * Lit la saisie : un entier de mois de 1 à 60 et un premier jour qui existe et n'est pas passé (la nouvelle tournée ne crée pas
 * d'interventions en retard) ; le rythme OU le jour doit changer. Les mêmes bornes que le serveur, dites en français.
 */
export function lireRythme(s: SaisieRythme, actuel: RythmeActuel, aujourdhui: string): LectureRythme {
  const texte = s.moisTexte.trim();
  if (texte === '') return { ok: false, erreur: `Choisissez le nouveau rythme, en mois (${MOIS_MIN} à ${MOIS_MAX}).` };
  if (!/^\d{1,3}$/.test(texte)) return { ok: false, erreur: `Le rythme est un nombre entier de mois, de ${MOIS_MIN} à ${MOIS_MAX}.` };
  const mois = Number(texte);
  if (mois < MOIS_MIN || mois > MOIS_MAX) return { ok: false, erreur: `Le rythme va de ${MOIS_MIN} à ${MOIS_MAX} mois.` };
  if (!jourValide(s.premierJour)) return { ok: false, erreur: 'Le premier jour de la nouvelle tournée n’existe pas : écrivez-le AAAA-MM-JJ.' };
  if (s.premierJour < aujourdhui) return { ok: false, erreur: 'Le premier jour de la nouvelle tournée ne peut pas être passé.' };
  if (memeRythmeEtMemeJour(actuel, mois, s.premierJour)) {
    return { ok: false, erreur: `Cette tournée est déjà ${libelleFrequence(mois)} à ce jour-là : changez le rythme ou le jour.` };
  }
  return { ok: true, frequencyMonths: mois, firstDay: s.premierJour };
}

/** Le corps envoyé : le jeton de la tournée, le rythme et le premier jour. Jamais un forfait, un prix ou une liste. */
export function corpsChangementRythme(revision: number, lu: Extract<LectureRythme, { ok: true }>): { revision: number; frequencyMonths: number; firstDay: string } {
  return { revision, frequencyMonths: lu.frequencyMonths, firstDay: lu.firstDay };
}

/** Ce que le geste va faire, dit AVANT : l'arrêt de l'ancienne (et ce qu'il annule) puis la nouvelle tournée. */
export function phraseEffetRythme(
  t: OccurrencesDeTournee,
  lu: Extract<LectureRythme, { ok: true }>,
  aujourdhui: string,
): string {
  const n = interventionsQuAnnuleraLArret(t.occurrences, aujourdhui);
  const arret =
    n === 0
      ? 'La tournée actuelle sera arrêtée (aucune intervention à venir à annuler).'
      : `La tournée actuelle sera arrêtée : ${n === 1 ? '1 intervention à venir s’annulera' : `${n} interventions à venir s’annuleront`}.`;
  return (
    `${arret} Les interventions déjà commencées, à contrôler, validées ou passées restent sur elle. ` +
    `Une nouvelle tournée ${libelleFrequence(lu.frequencyMonths)} démarre le ${jourFr(lu.firstDay)} (puis à ce jour-là du mois) : mêmes machines, mêmes techniciens habituels, ` +
    'même remarque, même n° de commande, même durée (et même forfait, s’il y en a un).'
  );
}

/** Le bilan du changement de rythme. */
export function phraseResultatRythme(r: Pick<TourneeRythmeChange, 'nouvelle' | 'interventionsAnnulees'>): string {
  const annulees =
    r.interventionsAnnulees === 0
      ? 'aucune intervention à venir annulée'
      : `${r.interventionsAnnulees === 1 ? '1 intervention à venir annulée' : `${r.interventionsAnnulees} interventions à venir annulées`}`;
  return `Tournée recréée : ${libelleFrequence(r.nouvelle.frequencyMonths)} depuis le ${jourFr(r.nouvelle.firstDay)} (${annulees} sur l’ancienne).`;
}

/** Les rythmes courants, en raccourcis (le rythme actuel compris : on peut ne changer que le jour). */
export const RYTHMES_RAPIDES: readonly number[] = [1, 3, 6, 12];
